import getAttributionParams from "@client/components/analytics/functions/getAttributionParams";

/**
 * Compares attribution parameters between two URLs to determine if they've changed
 * @param currentUrl - The current URL
 * @param previousUrl - The previous/ingress URL
 * @returns true if attribution params have changed, false otherwise
 */
export default function hasAttributionChanged(
	currentUrl: string,
	previousUrl: string,
): boolean {
	if (!currentUrl || !previousUrl) {
		return false;
	}

	const currentAttribution = getAttributionParams(currentUrl);
	const previousAttribution = getAttributionParams(previousUrl);

	// If current has attribution but previous doesn't, it's changed
	if (currentAttribution.size > 0 && previousAttribution.size === 0) {
		return true;
	}

	// If current has no attribution, no change
	if (currentAttribution.size === 0) {
		return false;
	}

	// Both have attribution - check if keys and values differ
	// Create a combined set of all keys from both URLs (auto-dedupes)
	const allKeys = new Set([
		...currentAttribution.keys(),
		...previousAttribution.keys(),
	]);

	// If either map has a different size than the combined set, keys differ
	if (
		currentAttribution.size !== allKeys.size ||
		previousAttribution.size !== allKeys.size
	) {
		return true;
	}

	// Same keys exist in both - now check if values match
	for (const [key, value] of currentAttribution.entries()) {
		if (previousAttribution.get(key) !== value) {
			return true;
		}
	}

	// No changes detected - same keys with same values
	return false;
}
